'use client';

import { useState, useEffect, useRef, useCallback, useSyncExternalStore, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Watchlist } from './Watchlist';
import { PriceChart } from './PriceChart';
import { KpiCard } from './KpiCard';
import { MarketMovers } from './MarketMovers';
import { NewsFeed } from './NewsFeed';
import { TerminalResearch, type Tab as ResearchTab } from './TerminalResearch';
import { MarketDataView, MARKET_DATA_TAB_ORDER } from './MarketDataView';
import { ShortcutsHelp } from './ShortcutsHelp';
import { TerminalNav, type TerminalViewType } from './TerminalNav';
import { TerminalWordmark } from './TerminalWordmark';
import { useTerminalShortcuts } from '@/lib/hooks/useTerminalShortcuts';
import { Search, Clock } from 'lucide-react';

interface SearchResult {
  symbol: string;
  name: string;
  exchange: string;
}

function LiveClock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);
  return (
    <span className="font-mono text-xs tabular-nums text-[#6B7280]">
      {time.toLocaleTimeString('en-US', { hour12: false })}
      <span className="text-[10px] ml-1 text-[#6B7280]/60">
        {time.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
      </span>
    </span>
  );
}

function SearchBar({ onSelectSymbol, onResearch, inputRef }: { onSelectSymbol: (s: string) => void; onResearch: (s: string) => void; inputRef: React.RefObject<HTMLInputElement | null> }) {
  const [input, setInput] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isAdding, setIsAdding] = useState<string | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const queryClient = useQueryClient();

  const searchStocks = useCallback(async (query: string) => {
    if (query.length < 1) {
      setResults([]);
      setShowDropdown(false);
      return;
    }
    try {
      const res = await fetch(`/api/stocks/search?q=${encodeURIComponent(query)}`);
      if (!res.ok) return;
      const data = await res.json();
      setResults(data);
      setShowDropdown(data.length > 0);
      setHighlightedIndex(-1);
    } catch {
      setResults([]);
    }
  }, []);

  const handleInputChange = (value: string) => {
    setInput(value.toUpperCase());
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchStocks(value), 250);
  };

  const addToWatchlist = async (result: SearchResult) => {
    setIsAdding(result.symbol);
    try {
      // Fetch existing portfolio
      const portfolioRes = await fetch('/api/portfolio');
      if (!portfolioRes.ok) throw new Error('Failed to fetch portfolio');
      const portfolio = await portfolioRes.json();
      const stocks = portfolio.stocks || {};

      // Check if already exists
      const existing = Object.values(stocks).find((s) => (s as { symbol?: string }).symbol === result.symbol);
      if (existing) {
        // Already in portfolio, just select it
        onSelectSymbol(result.symbol);
        setShowDropdown(false);
        setInput('');
        setIsAdding(null);
        return;
      }

      // Fetch stock data
      const stockRes = await fetch(`/api/stocks/${encodeURIComponent(result.symbol)}?includeProfile=true`);
      if (!stockRes.ok) throw new Error('Failed to fetch stock data');
      const stockData = await stockRes.json();

      // Create new watchlist stock entry
      const id = Math.random().toString(36).substr(2, 9);
      const newStock = {
        id,
        symbol: result.symbol,
        name: stockData.name || result.name,
        exchange: stockData.exchange || result.exchange,
        currency: stockData.currency || 'USD',
        currentPrice: stockData.currentPrice,
        historicalData: stockData.history || [],
        groundingUrls: stockData.groundingUrls || [],
        marketCap: stockData.marketCap || null,
        nextEarningsDate: stockData.nextEarningsDate || null,
        instrumentType: stockData.instrumentType || null,
        sector: stockData.sector || null,
        lastUpdated: new Date().toISOString(),
        userModifiedAt: new Date().toISOString(),
        isLoading: false,
        lots: [],
        watchlist: true,
      };

      // Save to portfolio
      const updatedStocks = { ...stocks, [id]: newStock };
      const saveRes = await fetch('/api/portfolio', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stocks: updatedStocks }),
      });
      if (!saveRes.ok) throw new Error('Failed to save');

      // Invalidate portfolio query so watchlist refreshes
      queryClient.invalidateQueries({ queryKey: ['/api/portfolio'] });
      onSelectSymbol(result.symbol);
    } catch (err) {
      console.error('Failed to add stock:', err);
    }
    setIsAdding(null);
    setShowDropdown(false);
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || results.length === 0) {
      if (e.key === 'Enter' && input.trim()) {
        e.preventDefault();
        onResearch(input.trim().toUpperCase());
        setInput('');
        setShowDropdown(false);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0) {
        addToWatchlist(results[highlightedIndex]);
      } else if (input.trim()) {
        onResearch(input.trim().toUpperCase());
        setInput('');
        setShowDropdown(false);
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
          inputRef.current && !inputRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [inputRef]);

  return (
    <div className="relative px-3 py-2 border-b border-[#1F2937]">
      <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-3 h-3 text-[#6B7280]" />
      <input
        ref={inputRef}
        type="text"
        value={input}
        onChange={(e) => handleInputChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (results.length > 0) setShowDropdown(true); }}
        placeholder="Search & add symbol..."
        className="w-full bg-[#1A2035] border border-[#1F2937] pl-7 pr-3 py-1.5 text-xs font-mono text-[#E5E7EB] placeholder:text-[#6B7280] focus:outline-none focus:border-[var(--color-accent)]"
      />
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-1 bg-[#111827] border border-[#1F2937] shadow-lg z-50 max-h-64 overflow-y-auto"
        >
          {results.map((r, i) => (
            <button
              key={r.symbol}
              onClick={() => addToWatchlist(r)}
              disabled={isAdding === r.symbol}
              className={`w-full text-left px-3 py-2 flex items-center justify-between transition-colors ${
                i === highlightedIndex ? 'bg-[#1A2035]' : 'hover:bg-[#1A2035]'
              } ${isAdding === r.symbol ? 'opacity-50' : ''}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs font-semibold text-[#E5E7EB]">{r.symbol}</span>
                  <span className="text-[10px] text-[#6B7280]">{r.exchange}</span>
                </div>
                <div className="text-[10px] text-[#6B7280] truncate">{r.name}</div>
              </div>
              <span className="text-[10px] text-[var(--color-accent)] ml-2 flex-shrink-0">
                {isAdding === r.symbol ? 'Adding...' : '+ Add'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export type View = { type: 'dashboard' } | { type: 'research'; symbol: string } | { type: 'market-data' };

// Nav-collapsed preference, exposed as an external store so components read
// localStorage via useSyncExternalStore (SSR-safe: the server snapshot is
// always "expanded" and React reconciles the client value after hydration).
const NAV_COLLAPSED_KEY = 'terminal-nav-collapsed';
// Tailwind's md breakpoint — keep in sync with the md: classes used in these views.
const MOBILE_QUERY = '(max-width: 767px)';
let navCollapsedListeners: Array<() => void> = [];

function subscribeNavCollapsed(listener: () => void) {
  navCollapsedListeners.push(listener);
  // With no stored preference the default depends on the viewport, so
  // re-snapshot when the breakpoint flips (resize, device rotation).
  const mql = window.matchMedia?.(MOBILE_QUERY);
  mql?.addEventListener('change', listener);
  return () => {
    navCollapsedListeners = navCollapsedListeners.filter((l) => l !== listener);
    mql?.removeEventListener('change', listener);
  };
}

function getNavCollapsed(): boolean {
  const stored = window.localStorage.getItem(NAV_COLLAPSED_KEY);
  if (stored !== null) return stored === '1';
  // No saved preference — default to collapsed on small screens where width is scarce
  return window.matchMedia?.(MOBILE_QUERY)?.matches ?? false;
}

function setNavCollapsedPref(collapsed: boolean) {
  window.localStorage.setItem(NAV_COLLAPSED_KEY, collapsed ? '1' : '0');
  navCollapsedListeners.forEach((l) => l());
}

export function TerminalDashboard({ initialView }: { initialView?: View } = {}) {
  const [selectedSymbol, setSelectedSymbol] = useState('AAPL');
  const [view, setView] = useState<View>(initialView || { type: 'dashboard' });
  const [researchTab, setResearchTab] = useState<ResearchTab>('Overview');
  const [marketDataTab, setMarketDataTab] = useState<string>(MARKET_DATA_TAB_ORDER[0]);
  const [helpOpen, setHelpOpen] = useState(false);
  const navCollapsed = useSyncExternalStore(subscribeNavCollapsed, getNavCollapsed, () => false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const toggleNav = () => setNavCollapsedPref(!navCollapsed);

  const { data: macro, isLoading: macroLoading } = useQuery<{
    indices: Record<string, { price: number; change: number; changePercent: number; sparkline: number[] }>;
  }>({
    queryKey: ['/api/terminal/macro'],
    queryFn: async () => {
      const res = await fetch('/api/terminal/macro');
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    refetchInterval: 60000,
  });

  // Watchlist reports its sorted symbol order up so [/] cycling matches what the user sees.
  const [watchlistSymbols, setWatchlistSymbols] = useState<string[]>([]);

  const enterResearch = useCallback(
    (symbol: string) => {
      setResearchTab('Overview');
      setView({ type: 'research', symbol });
    },
    []
  );

  // The symbol the user is currently looking at: the research view's symbol,
  // or the watchlist selection everywhere else. Drives Research navigation
  // (nav rail and `g r`) and watchlist cycling.
  const currentSymbol = view.type === 'research' ? view.symbol : selectedSymbol;

  useTerminalShortcuts({
    view: view.type,
    onFocusSearch: () => {
      // If we're not on the dashboard, return there first so the input is mounted
      if (view.type !== 'dashboard') setView({ type: 'dashboard' });
      // Focus on next tick so the input has rendered
      requestAnimationFrame(() => searchInputRef.current?.focus());
    },
    onEscape: () => setView({ type: 'dashboard' }),
    onGoTo: (where) => {
      if (where === 'dashboard') setView({ type: 'dashboard' });
      else if (where === 'research') enterResearch(currentSymbol);
      else if (where === 'market-data') setView({ type: 'market-data' });
    },
    onCycleWatchlist: (delta) => {
      if (watchlistSymbols.length === 0) return;
      // Cycle relative to whatever the user is currently looking at.
      const idx = watchlistSymbols.indexOf(currentSymbol);
      const nextIdx = idx === -1
        ? 0
        : (idx + delta + watchlistSymbols.length) % watchlistSymbols.length;
      const next = watchlistSymbols[nextIdx];
      setSelectedSymbol(next);
      if (view.type === 'research') setView({ type: 'research', symbol: next });
    },
    onSetResearchTab: (i) => {
      const tab = (['Overview', 'Financials', 'Risk'] as const)[i];
      if (tab) setResearchTab(tab);
    },
    onSetMarketDataTab: (i) => {
      const tab = MARKET_DATA_TAB_ORDER[i];
      if (tab) setMarketDataTab(tab);
    },
    onShowHelp: () => setHelpOpen(true),
  });

  const indices = macro?.indices || {};

  const navigate = (where: TerminalViewType) => {
    if (where === 'dashboard') setView({ type: 'dashboard' });
    else if (where === 'research') enterResearch(currentSymbol);
    else setView({ type: 'market-data' });
  };

  let content: ReactNode;
  if (view.type === 'research') {
    content = (
      <TerminalResearch
        symbol={view.symbol}
        onBack={() => setView({ type: 'dashboard' })}
        tab={researchTab}
        onTabChange={setResearchTab}
      />
    );
  } else if (view.type === 'market-data') {
    content = (
      <MarketDataView
        onBack={() => setView({ type: 'dashboard' })}
        onResearch={enterResearch}
        activeTab={marketDataTab}
        onTabChange={setMarketDataTab}
      />
    );
  } else {
    content = (
      <div className="h-full flex flex-col">
        {/* Top Bar */}
        <div className="h-12 flex items-center justify-between px-3 md:px-4 bg-[#111827] border-b border-[#1F2937] flex-shrink-0">
          <div className="flex items-center gap-3">
            <TerminalWordmark />
            <span className="relative group">
              <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-500 border border-yellow-500/30 cursor-default">
                Alpha
              </span>
              <span className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-2.5 py-1.5 bg-[#111827] border border-[#1F2937] text-[10px] text-[#E5E7EB] whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-50 shadow-lg">
                This view is still in development, there may be issues
              </span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-3 h-3 text-[#6B7280]" />
            <LiveClock />
          </div>
        </div>

        {/* Main Content — stacks vertically on mobile, sidebar + main from md up */}
        <div className="flex-1 flex flex-col md:flex-row min-h-0 overflow-y-auto md:overflow-visible">
          {/* Left Sidebar — Search + Watchlist */}
          <div className="w-full md:w-[280px] border-b md:border-b-0 md:border-r border-[#1F2937] bg-[#0D1117] flex-shrink-0 flex flex-col">
            <SearchBar
              inputRef={searchInputRef}
              onSelectSymbol={setSelectedSymbol}
              onResearch={enterResearch}
            />
            <Watchlist
              onSelectSymbol={(s) => setSelectedSymbol(s)}
              onSortedSymbolsChange={setWatchlistSymbols}
            />
          </div>

          {/* Main Area */}
          <div className="flex-1 flex flex-col min-w-0 md:min-h-0 md:overflow-y-auto">
            {/* KPI Row — Major Indices */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#1F2937] flex-shrink-0">
              <KpiCard
                label="S&P 500"
                price={indices.SPY?.price || 0}
                change={indices.SPY?.change || 0}
                changePercent={indices.SPY?.changePercent || 0}
                sparkline={indices.SPY?.sparkline}
                isLoading={macroLoading}
              />
              <KpiCard
                label="NASDAQ"
                price={indices.QQQ?.price || 0}
                change={indices.QQQ?.change || 0}
                changePercent={indices.QQQ?.changePercent || 0}
                sparkline={indices.QQQ?.sparkline}
                isLoading={macroLoading}
              />
              <KpiCard
                label="DOW"
                price={indices.DIA?.price || 0}
                change={indices.DIA?.change || 0}
                changePercent={indices.DIA?.changePercent || 0}
                sparkline={indices.DIA?.sparkline}
                isLoading={macroLoading}
              />
              <KpiCard
                label="RUSSELL"
                price={indices.IWM?.price || 0}
                change={indices.IWM?.change || 0}
                changePercent={indices.IWM?.changePercent || 0}
                sparkline={indices.IWM?.sparkline}
                isLoading={macroLoading}
              />
            </div>

            {/* Chart Area */}
            <div className="flex-1 p-4 min-h-[320px]">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-mono text-sm font-bold text-[#E5E7EB]">{selectedSymbol}</span>
                <span className="text-[10px] text-[#6B7280] uppercase tracking-wider">Price Chart</span>
              </div>
              <PriceChart symbol={selectedSymbol} height={260} />
            </div>

            {/* Bottom Panels */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#1F2937] flex-shrink-0">
              {/* Market Movers */}
              <div className="bg-[#0D1117]">
                <div className="px-3 py-2 border-b border-[#1F2937]">
                  <span className="text-[10px] font-semibold text-[var(--color-accent)] uppercase tracking-wider">Market Movers</span>
                </div>
                <MarketMovers />
              </div>

              {/* News */}
              <div className="bg-[#0D1117]">
                <div className="px-3 py-2 border-b border-[#1F2937]">
                  <span className="text-[10px] font-semibold text-[var(--color-accent)] uppercase tracking-wider">News</span>
                </div>
                <NewsFeed symbol={selectedSymbol} limit={6} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex">
      <TerminalNav
        active={view.type}
        symbol={currentSymbol}
        onNavigate={navigate}
        onShowHelp={() => setHelpOpen(true)}
        collapsed={navCollapsed}
        onToggleCollapsed={toggleNav}
      />
      <div className="flex-1 min-w-0 h-full">{content}</div>
      <ShortcutsHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}
